import { types, flow, SnapshotOut, Instance } from 'mobx-state-tree'

import { DataStore } from './data-store/data-store'
import { JobScheduleType } from './job'
import { PaginatorModel } from './common'

export enum ApplicantStatus {
  PENDING = 1,
  SHORTLISTED = 2,
  REJECTED = 3,
}

const SkillModel = types.model('SkillModel', {
  id: types.identifierNumber,
  name: types.string,
  rating: types.number,
  work_category_name: types.string,
  icon: types.maybeNull(types.string),
  is_matched: types.boolean,
})

const CheeryPickingApplicantModel = types
  .model('CheeryPickingApplicant', {
    applicant_id: types.identifierNumber,
    applicant_name: types.string,
    image_url: types.maybeNull(types.string),
    rating: types.maybeNull(types.number),
    nationality: types.maybeNull(types.string),
    years_experience: types.number,
    seniority_level: types.model({
      name: types.string,
      id: types.number,
    }),
    past_jobs: types.array(
      types.model({
        id: types.number,
        designation_name: types.string,
        location_name: types.string,
        start_date: types.string,
        end_date: types.maybeNull(types.string),
      }),
    ),
    applicant_languages: types.array(types.string),
    age: types.maybeNull(types.number),
    height: types.maybeNull(types.number),
    completed_shifts_count: types.number,
    top_qualities: types.maybeNull(types.array(types.string)),
    is_favorite: 0 | 1,
    is_accepted: 0 | 1,
    is_chief: types.boolean,
    skills: types.array(SkillModel),
    skillMatch: types.number,
  })
  .views(self => ({
    get id() {
      return self.applicant_id
    },
    get image() {
      return self.image_url
    },
    get name() {
      return self.applicant_name
    },
    get languages() {
      return self.applicant_languages
    },
    get isAccepted() {
      return self.is_accepted === 1
    },
    get isRejected() {
      return false
    },
  }))

const CherryPickingApplicantOfPermanentJobModel = types
  .model('CherryPickingApplicantOfPermanentJob', {
    job_application_id: types.identifierNumber,
    client_status_id: types.number, // ApplicantStatus
    full_name: types.maybeNull(types.string),
    profile_photo: types.maybeNull(types.string),
    fullbody_photo: types.maybeNull(types.string),
    age: types.maybeNull(types.number),
    spoken_languages: types.array(types.string),
    nationality: types.maybeNull(types.string),
    location: types.maybeNull(types.string),
    gender: types.maybeNull(types.string),
    education: types.maybeNull(types.string),
    video: types.maybeNull(types.string),
    experience: types.maybeNull(
      types.array(
        types.model('ApplicantExperience', {
          work_category_name: types.maybeNull(types.string),
          company_name: types.maybeNull(types.string),
          location_name: types.maybeNull(types.string),
          description: types.maybeNull(types.string),
          duration: types.maybeNull(types.string),
        }),
      ),
    ),
  })
  .views(self => ({
    get id() {
      return self.job_application_id
    },
    get image() {
      return self.profile_photo
    },
    get name() {
      return self.full_name
    },
    get languages() {
      return self.spoken_languages
    },
    get isAccepted() {
      return self.client_status_id === ApplicantStatus.SHORTLISTED
    },
    get isRejected() {
      return self.client_status_id === ApplicantStatus.REJECTED
    },
  }))

export const CherryPickingApplicantsModel = types.model('CherryPickingApplicants', {
  list: types.optional(types.map(CheeryPickingApplicantModel), {}),
  currentApplicant: types.maybeNull(types.reference(CheeryPickingApplicantModel)),
  paginator: types.maybeNull(PaginatorModel),
})

export const CherryPickingApplicantsOfPermanentJobModel = types.model('CherryPickingApplicantsOfPermanentJob', {
  list: types.optional(types.map(CherryPickingApplicantOfPermanentJobModel), {}),
  currentApplicant: types.maybeNull(types.reference(CherryPickingApplicantOfPermanentJobModel)),
  paginator: types.maybeNull(PaginatorModel),
})

export const cherryPickingApplicantsActions = (self: DataStore) => ({
  getCherryPickingApplicantsApiCall: flow(function* ({
    jobId,
    jobType,
    page = 1,
    sortBy,
    sortDirection = 'DESC',
    shiftId,
  }: {
    jobId: number
    jobType: JobScheduleType
    page?: number
    sortBy?: string
    sortDirection?: string
    shiftId?: number
  }) {
    if (jobType === JobScheduleType.permanent) {
      const response = yield self.request(
        'get',
        `client/cherry-picking/permanent-jobs/${jobId}/applicants`,
        undefined,
        {
          params: {
            page,
            sort_by: sortBy,
            sort_direction: sortDirection,
          },
        },
      )
      if (page === 1) {
        self.cherryPickingApplicantsOfPermanentJob.list.clear()
        self.cherryPickingApplicantsOfPermanentJob.currentApplicant = null
      }
      response.list.forEach((applicant: CherryPickingApplicantOfPermanentJob) => {
        const age = applicant.age !== null ? Number(applicant.age) : null
        self.cherryPickingApplicantsOfPermanentJob.list.set(String(applicant.job_application_id), {
          ...applicant,
          age,
        })
      })
      self.cherryPickingApplicantsOfPermanentJob.paginator = response.paginator
    } else {
      const response = yield self.request('get', 'client/cherry-picking/applicants', undefined, {
        params: {
          job_id: jobId,
          shift_id: shiftId,
          sort_by: sortBy,
          sort_direction: sortDirection,
          page,
        },
      })
      if (page === 1) {
        self.cherryPickingApplicants.list.clear()
        self.cherryPickingApplicants.currentApplicant = null
      }
      response.list.forEach((applicant: CherryPickingApplicant) => {
        self.cherryPickingApplicants.list.set(String(applicant.id), {
          ...applicant,
          applicant_id: applicant.id,
          applicant_name: applicant.name,
          applicant_languages: applicant.languages,
        })
      })
      self.cherryPickingApplicants.paginator = response.paginator
    }
  }),
  acceptApplicantApiCall: flow(function* ({
    job_id,
    sp_id,
    shift_ids,
  }: {
    job_id: number
    sp_id: string
    shift_ids?: number[]
  }) {
    const response = yield self.request('post', `client/cherry-picking/applicants/accept`, {
      job_id,
      sp_id,
      shift_ids,
    })
    const job = self.cherryPickingJobs.map.get(String(job_id))
    if (job) {
      job.shifts_fulfilled_count = response.shifts_fulfilled_count
      self.cherryPickingJobs.map.set(String(job.id), job)
    }
    const applicant = self.cherryPickingApplicants.list.get(sp_id)
    if (applicant) {
      applicant.is_accepted = 1
      self.cherryPickingApplicants.list.set(String(applicant.id), applicant)
    }
  }),
  acceptApplicantOfPermanentJob: flow(function* ({ job_application_id }: { job_application_id: number }) {
    yield self.request('post', 'client/cherry-picking/permanent-jobs/applicants/change-status', {
      job_application_id,
      client_status_id: ApplicantStatus.SHORTLISTED,
    })
    const applicant = self.cherryPickingApplicantsOfPermanentJob.list.get(job_application_id)
    if (applicant) {
      applicant.client_status_id = ApplicantStatus.SHORTLISTED
    }
  }),
  rejectApplicantApiCall: flow(function* ({ job_id, sp_id }: { job_id: number; sp_id: string }) {
    yield self.request('post', `client/cherry-picking/applicants/reject`, {
      job_id,
      sp_id,
    })
    self.cherryPickingApplicants.currentApplicant = null
    self.cherryPickingApplicants.list.delete(sp_id)
    if (self.cherryPickingApplicants.paginator) {
      self.cherryPickingApplicants.paginator.total = self.cherryPickingApplicants.paginator.total - 1
    }
  }),
  rejectApplicantOfPermanentJob: flow(function* ({ job_application_id }: { job_application_id: number }) {
    yield self.request('post', 'client/cherry-picking/permanent-jobs/applicants/change-status', {
      job_application_id,
      client_status_id: ApplicantStatus.REJECTED,
    })
    const applicant = self.cherryPickingApplicantsOfPermanentJob.list.get(job_application_id)
    if (applicant) {
      applicant.client_status_id = ApplicantStatus.REJECTED
    }
  }),
  toggleApplicantAsFavouriteApiCall: flow(function* (sp_id: string) {
    const applicant = self.cherryPickingApplicants.list.get(sp_id)
    if (applicant) {
      if (applicant.is_favorite) {
        applicant.is_favorite = 0
        yield self.request('post', 'client/sp/un-save', { sp_id })
      } else {
        applicant.is_favorite = 1
        yield self.request('post', 'client/sp/save', { sp_id })
      }
      self.cherryPickingApplicants.list.set(String(applicant.id), applicant)
    }
  }),
  toggleCurrentApplicantAsFavouriteApiCall: flow(function* () {
    const applicant = self.cherryPickingApplicants.currentApplicant
    if (applicant) {
      if (applicant.is_favorite) {
        applicant.is_favorite = 0
        yield self.request('post', 'client/sp/un-save', { sp_id: applicant.id })
      } else {
        applicant.is_favorite = 1
        yield self.request('post', 'client/sp/save', { sp_id: applicant.id })
      }
    }
  }),
  getApplicant: flow(function* (jobId: number, spId: number) {
    const currentApplicant = (yield self.request(
      'get',
      `client/cherry-picking/jobs/${jobId}/applicants/${spId}`,
    )) as CherryPickingApplicant

    if (!self.cherryPickingApplicants.list.has(currentApplicant.id)) {
      self.cherryPickingApplicants.list.set(String(currentApplicant.id), {
        ...currentApplicant,
        applicant_id: currentApplicant.id,
        applicant_name: currentApplicant.name,
        applicant_languages: currentApplicant.languages,
      })
    }
    self.cherryPickingApplicants.currentApplicant = self.cherryPickingApplicants.list.get(
      currentApplicant.id,
    ) as CherryPickingApplicant

    return currentApplicant
  }),
  setCurrentApplicant(applicantId: number, jobType: JobScheduleType) {
    if (jobType === JobScheduleType.permanent) {
      const applicant = self.cherryPickingApplicantsOfPermanentJob.list.get(String(applicantId))
      if (!applicant) {
        throw new Error(`Applicant with id ${applicantId} not found`)
      }
      self.cherryPickingApplicantsOfPermanentJob.currentApplicant = applicant
      return
    }
    const applicant = self.cherryPickingApplicants.list.get(String(applicantId))
    if (!applicant) {
      throw new Error(`Applicant with id ${applicantId} not found`)
    }
    self.cherryPickingApplicants.currentApplicant = applicant
  },
  clearCurrentApplicant(jobType: JobScheduleType) {
    if (jobType === JobScheduleType.permanent) {
      self.cherryPickingApplicantsOfPermanentJob.currentApplicant = null
    } else {
      self.cherryPickingApplicants.currentApplicant = null
    }
  },
})

export const cherryPickingApplicantsViews = (self: DataStore) => ({
  applicantsView(jobType: JobScheduleType) {
    if (jobType === JobScheduleType.permanent) {
      return {
        list: Array.from(self.cherryPickingApplicantsOfPermanentJob.list.values()),
        paginator: self.cherryPickingApplicantsOfPermanentJob.paginator,
      }
    }
    return {
      list: Array.from(self.cherryPickingApplicants.list.values()),
      paginator: self.cherryPickingApplicants.paginator,
    }
  },

  getApplicantById(id: number, jobType: JobScheduleType) {
    if (jobType === JobScheduleType.permanent) {
      return Array.from(self.cherryPickingApplicantsOfPermanentJob.list.values()).find(
        item => item.job_application_id === id,
      )
    }
    return Array.from(self.cherryPickingApplicants.list.values()).find(item => item.id === id) as
      | CherryPickingApplicantSnapshot
      | undefined
  },

  getNotAcceptedApplicantsList(jobType: JobScheduleType) {
    if (jobType === JobScheduleType.permanent) {
      return Array.from(self.cherryPickingApplicantsOfPermanentJob.list.values()).filter(
        applicant => applicant.client_status_id !== ApplicantStatus.SHORTLISTED,
      )
    }
    return Array.from(self.cherryPickingApplicants.list.values()).filter(applicant => applicant.is_accepted === 0)
  },

  getCurrentApplicant(jobType: JobScheduleType) {
    if (jobType === JobScheduleType.permanent) {
      return self.cherryPickingApplicantsOfPermanentJob.currentApplicant
    }
    return self.cherryPickingApplicants.currentApplicant
  },

  get skillsGroupedByCategory() {
    const applicant = self.cherryPickingApplicants.currentApplicant
    if (!applicant) {
      return []
    }

    const groupedSkills = applicant.skills.reduce(
      (result: { [key: string]: { workCategoryName: string; skills: SkillSnapshot[] } }, skill: SkillSnapshot) => {
        const workCategoryName = skill.work_category_name
        // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
        if (!result[workCategoryName]) {
          result[workCategoryName] = {
            workCategoryName,
            skills: [],
          }
        }

        result[workCategoryName].skills.push(skill)

        return result
      },
      {},
    )

    return Object.values(groupedSkills)
  },
})

export type CherryPickingApplicant = Instance<typeof CheeryPickingApplicantModel>
export type CherryPickingApplicantOfPermanentJob = Instance<typeof CherryPickingApplicantOfPermanentJobModel>
export type CherryPickingApplicants = Instance<typeof CherryPickingApplicantsModel>
export type CherryPickingApplicantSnapshot = SnapshotOut<typeof CheeryPickingApplicantModel>
export type CherryPickingApplicantOfPermanentJobSnapshot = SnapshotOut<typeof CherryPickingApplicantOfPermanentJobModel>
export type CherryPickingJobSnapshot = SnapshotOut<typeof CherryPickingApplicantsModel>
export type SkillSnapshot = SnapshotOut<typeof SkillModel>
